import React from 'react';
import Contact from './Contact';

const KidsphereDetail = () => {
  const icon = (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
  );

  const benefits = [
    { icon: '🎯', title: 'Engaging Learning', description: 'Interactive activities keep kids motivated and excited to learn' },
    { icon: '📊', title: 'Progress Tracking', description: 'Real-time analytics help parents monitor their child\'s growth' },
    { icon: '🏆', title: 'Gamified Experience', description: 'Rewards, badges, and leaderboards make learning fun' },
    { icon: '📱', title: 'Accessible Everywhere', description: 'Available on web and mobile with offline support' }
  ];

  const keyFeatures = [
    'Mobile App Available - Download Android app from Google Play Store',
    'Interactive Lessons - 4 unique activity types with voice-enabled support',
    'Smart Activity System - Hints, retry options, and instant feedback',
    'Progress Tracking - Real-time XP system, daily streaks, and level progression',
    'Leaderboard - Compete with peers on global, weekly, and monthly rankings',
    'Rewards & Badges - Milestone celebrations and achievement badges',
    'Daily Goals - Customizable XP targets to build consistent learning habits',
    'Bonus Events - Special events with double XP and streak boosts',
    'Parent Dashboard - Comprehensive analytics and progress monitoring',
    'Voice-Enabled Learning - Speech-to-text and text-to-speech support',
    'Subject-Based Learning - Organized by 6+ subjects with personalized paths',
    'Safe & Secure - COPPA-compliant with parent-controlled access'
  ];

  const lifeSkills = [
    {
      title: 'Empathy & Social Skills',
      description: 'Building emotional intelligence and strong interpersonal relationships through guided activities and real-world scenarios.',
      items: ['Understanding emotions', 'Building relationships', 'Developing compassion', 'Emotional intelligence', 'Active listening', 'Conflict resolution', 'Building friendships', 'Sharing and cooperation']
    },
    {
      title: 'Financial Literacy',
      description: 'Teaching kids smart money habits and financial decision-making through engaging, age-appropriate lessons.',
      items: ['Smart money habits', 'Saving strategies', 'Smart spending', 'Needs vs. wants', 'Value of money', 'Budgeting basics']
    },
    {
      title: 'Digital Safety',
      description: 'Preparing children for the digital world with essential online safety and digital citizenship skills.',
      items: ['Online privacy', 'Cyberbullying prevention', 'Digital citizenship', 'Screen time management', 'Critical thinking about content']
    },
    {
      title: 'Communication Skills',
      description: 'Developing effective communication abilities that help children express themselves clearly and confidently.',
      items: ['Active listening', 'Expressing feelings', 'Asking for help', 'Public speaking', 'Non-verbal communication']
    },
    {
      title: 'Resilience & Problem Solving',
      description: 'Building mental strength and creative problem-solving capabilities for life\'s challenges.',
      items: ['Handling mistakes', 'Managing frustration', 'Growth mindset', 'Decision making', 'Creative problem solving']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Sign Up & Create Profile',
      description: 'Parents create a secure account and set up their child\'s profile. Customize learning preferences, set daily goals, and choose age-appropriate content. The platform guides you through an easy onboarding process.',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80',
      tasks: ['Parent account creation', 'Child profile setup', 'Age-appropriate content selection', 'Learning preferences configuration']
    },
    {
      step: '02',
      title: 'Explore Subjects & Missions',
      description: 'Kids explore 6+ subjects with engaging animated stories and guided missions. Content is organized by age groups and learning levels. Each mission is designed to teach specific life skills through interactive storytelling.',
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&q=80',
      tasks: ['Subject exploration', 'Mission selection', 'Animated story engagement', 'Interactive learning activities']
    },
    {
      step: '03',
      title: 'Complete Interactive Activities',
      description: 'Kids engage with 4 unique activity types: Multiple Choice quizzes, Arrange Words, Match Pairs, and Unscramble Word puzzles. Voice-enabled activities provide accessibility, while hints and retry options ensure learning success.',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80',
      tasks: ['Activity completion', 'Multiple choice quizzes', 'Interactive games', 'Voice-enabled interactions']
    },
    {
      step: '04',
      title: 'Earn Rewards & Track Progress',
      description: 'Kids earn XP points, unlock badges, and see their progress in real-time. Daily streaks keep them motivated, while the leaderboard adds friendly competition. Parents can monitor achievements through the dashboard.',
      image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&q=80',
      tasks: ['XP earning system', 'Badge unlocking', 'Progress tracking', 'Achievement celebrations']
    },
    {
      step: '05',
      title: 'Participate in Bonus Events',
      description: 'Special bonus events with double XP, bonus XP, and streak boosts make learning even more exciting. Events are automatically applied to keep kids engaged and motivated to learn daily.',
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&q=80',
      tasks: ['Event participation', 'Double XP periods', 'Streak boosts', 'Reward multipliers']
    },
    {
      step: '06',
      title: 'Monitor & Celebrate Growth',
      description: 'Parents access comprehensive analytics showing completed lessons, achievements, leaderboard rankings, and detailed learning progress. Celebrate milestones and track long-term skill development.',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80',
      tasks: ['Parent dashboard access', 'Progress analytics', 'Achievement tracking', 'Long-term growth monitoring']
    }
  ];

  const technologies = ['React', 'Node.js', 'MongoDB', 'React Native', 'Mobile App Development', 'Gamification', 'Analytics', 'Speech Recognition'];

  return (
    <>
      {/* Hero Section */}
      <section className="service-hero-section">
        <div className="service-hero-background" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1600&q=80')` }}></div>
        <div className="service-hero-overlay"></div>
        <div className="container">
          <div className="service-hero-content">
            <div className="service-hero-icon">{icon}</div>
            <div style={{ marginBottom: '1rem' }}>
              <img src="/kidsphere logo/logo.png" alt="Kidsphere" style={{ maxWidth: '200px', height: 'auto', marginBottom: '1rem' }} />
          </div>
            <h1 className="service-hero-title">Making Kids Fall in Love with Life Skills!</h1>
            <p className="service-hero-subtitle">Revolutionary EdTech Platform for Life Skills Learning</p>
            <p className="service-hero-description">
              Guided missions, animated stories, and positive reinforcement help kids build life-skills while parents track progress in real-time. Available on web and mobile with offline support. Built by CodeRidge with the same production discipline we apply to cloud migrations, zero-trust security, and 24/7 managed operations—privacy-forward, observable, and ready to grow.
            </p>
            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <a 
              href="https://kidsphere.in" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-primary btn-large"
            >
              Start Learning for Free
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginLeft: '0.5rem' }}>
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
            <a 
              href="https://kidsphere.in" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-secondary btn-large"
                style={{ background: 'rgba(255, 255, 255, 0.1)', color: 'white', border: '2px solid rgba(255, 255, 255, 0.3)' }}
            >
              Download Mobile App
            </a>
          </div>
        </div>
      </div>
      </section>

      {/* Benefits Section */}
      <section className="service-benefits-section">
        <div className="container">
          <div className="service-benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="service-benefit-card">
                <div className="service-benefit-icon">{benefit.icon}</div>
                <h3 className="service-benefit-title">{benefit.title}</h3>
                <p className="service-benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="service-features-detail-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Key Features</span>
            <h2 className="section-title">Everything Kids Need to Learn & Grow</h2>
            <p className="section-description">
              Comprehensive features designed to make learning life skills engaging, fun, and effective
            </p>
          </div>
          <div className="features-grid-detail">
            {keyFeatures.map((feature, index) => (
              <div key={index} className="feature-card-detail">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="feature-check-icon">
                  <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="feature-text-detail">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Life Skills Section */}
      <section className="service-process-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Life Skills</span>
            <h2 className="section-title">Essential Skills We Teach</h2>
            <p className="section-description">
              Building well-rounded individuals through comprehensive life skills education
            </p>
          </div>
          <div className="service-process-steps">
            {lifeSkills.map((skill, index) => (
              <div key={index} className={`service-process-step ${index % 2 === 1 ? 'process-step-reverse' : ''}`}>
                <div className="process-step-image-wrapper">
                  <img 
                    src={`https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80&sig=${index}`} 
                    alt={skill.title}
                    className="process-step-image"
                    onError={(e) => {
                      e.target.src = `https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&q=80`;
                    }}
                  />
                  <div className="process-step-number">{String(index + 1).padStart(2, '0')}</div>
                </div>
                <div className="process-step-content">
                  <h3 className="process-step-title">{skill.title}</h3>
                  <p className="process-step-description">{skill.description}</p>
                  <ul className="process-step-tasks">
                    {skill.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          </div>
        </section>

      {/* How It Works Process Section */}
      <section className="service-process-section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="section-header">
            <span className="section-badge">How It Works</span>
            <h2 className="section-title">Get Started in 6 Simple Steps</h2>
            <p className="section-description">
              A simple, guided journey from sign-up to mastering life skills
            </p>
          </div>
          <div className="service-process-steps">
            {process.map((step, index) => (
              <div key={index} className="service-process-step">
                <div className="process-step-image-wrapper">
                  <img src={step.image} alt={step.title} className="process-step-image" />
                  <div className="process-step-number">{step.step}</div>
                </div>
                <div className="process-step-content">
                  <h3 className="process-step-title">{step.title}</h3>
                  <p className="process-step-description">{step.description}</p>
                  <ul className="process-step-tasks">
                    {step.tasks.map((task, taskIndex) => (
                      <li key={taskIndex}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {task}
                      </li>
                  ))}
                </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="service-technologies-detail-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Tech Stack</span>
            <h2 className="section-title">Built with Modern Technology</h2>
            <p className="section-description">
              Powered by cutting-edge technologies for seamless learning experiences
            </p>
          </div>
          <div className="technologies-grid-detail">
            {technologies.map((tech, index) => (
              <div key={index} className="tech-card-detail">
                <span className="tech-name-detail">{tech}</span>
              </div>
            ))}
          </div>
          </div>
        </section>

      {/* CTA Section */}
      <section className="service-cta-detail-section">
        <div className="container">
          <div className="service-cta-content">
            <h2 className="service-cta-title">Ready to Start Learning?</h2>
            <p className="service-cta-description">
              Join thousands of families building brighter futures together. Features leaderboard, daily goals, bonus events, and offline learning. Available on web and mobile!
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <a 
              href="https://kidsphere.in" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-primary btn-large"
            >
              Start Free Trial Today
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginLeft: '0.5rem' }}>
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </a>
            <a 
              href="https://kidsphere.in" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-secondary btn-large"
            >
              Download Mobile App
            </a>
          </div>
      </div>
    </div>
      </section>

      <Contact />
    </>
  );
};

export default KidsphereDetail;
